import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Spinner, Alert, Modal, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faTasks,
  faPlay,
  faPause,
  faEye,
  faCalendarAlt,
  faUser,
  faPaperPlane,
  faEnvelope,
  faPhone,
  faFileText,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface WorkflowTask {
  task_id: string;
  agent_type: string;
  action: string;
  priority: string;
  scheduled_for: string;
  status: 'pending' | 'completed' | 'failed' | 'executing';
  requires_approval: boolean;
  task_data: any;
  ai_response?: string;
  created_at: string;
  executed_at?: string;
  completed_at?: string;
  error_message?: string;
}

interface WorkflowInstance {
  instance_id: string;
  lead_id: string;
  workflow_template_id: string;
  current_stage_id: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  automation_level: string;
  metadata: any;
  created_at: string;
  lead_first_name: string;
  lead_last_name: string;
  lead_email: string;
  lead_score: number;
}

interface WorkflowMetrics {
  total_tasks: number;
  completed_tasks: number;
  pending_tasks: number;
  failed_tasks: number;
  progress_percentage: number;
}

interface WorkflowData {
  workflow: WorkflowInstance | null;
  tasks: WorkflowTask[];
  metrics: WorkflowMetrics;
}

interface AgentKairosWorkflowPanelProps {
  leadId: string;
  className?: string;
  refreshTrigger?: number;
}

const AgentKairosWorkflowPanel: React.FC<AgentKairosWorkflowPanelProps> = ({
  leadId,
  className = '',
  refreshTrigger = 0
}) => {
  const [loading, setLoading] = useState(true);
  const [workflowData, setWorkflowData] = useState<WorkflowData | null>(null);
  const [error, setError] = useState<string>('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<WorkflowTask | null>(null);
  const [approvingTasks, setApprovingTasks] = useState<Set<string>>(new Set());

  // Fetch workflow data from Agent Kairos API
  const fetchWorkflowData = async () => {
    if (!leadId) return;

    setLoading(true);
    setError('');

    try {
      console.log('🤖 [AGENT KAIROS] Fetching workflow data for lead:', leadId);

      const response = await axiosInstance.get(`/agent-kairos/leads/${leadId}/workflow`);
      
      if (response.data.success) {
        console.log('✅ [AGENT KAIROS] Workflow data loaded:', response.data.data);
        setWorkflowData(response.data.data);
      } else {
        console.log('⚠️ [AGENT KAIROS] No workflow found for lead');
        setWorkflowData(null);
      }
    } catch (error: any) {
      console.error('❌ [AGENT KAIROS] Failed to fetch workflow data:', error);
      setError(error.response?.data?.message || 'Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or leadId changes
  useEffect(() => {
    fetchWorkflowData();
  }, [leadId, refreshTrigger]);

  // Approve a task
  const approveTask = async (taskId: string) => {
    setApprovingTasks(prev => new Set([...prev, taskId]));

    try {
      console.log('✅ [AGENT KAIROS] Approving task:', taskId);
      
      const response = await axiosInstance.post(`/agent-kairos/tasks/${taskId}/approve`);
      
      if (response.data.success) {
        toast.success('✅ Task approved! It will be executed automatically.');
        
        // Refresh workflow data to show updated status
        setTimeout(fetchWorkflowData, 1000);
      } else {
        toast.error('Failed to approve task');
      }
    } catch (error: any) {
      console.error('❌ [AGENT KAIROS] Failed to approve task:', error);
      toast.error(error.response?.data?.message || 'Failed to approve task');
    } finally {
      setApprovingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  // Pause/Resume workflow
  const toggleWorkflowStatus = async (workflowId: string, currentStatus: string) => {
    try {
      const action = currentStatus === 'ACTIVE' ? 'pause' : 'resume';
      const response = await axiosInstance.post(`/agent-kairos/workflows/${workflowId}/${action}`);
      
      if (response.data.success) {
        toast.success(`Workflow ${action}d successfully`);
        fetchWorkflowData();
      }
    } catch (error: any) {
      console.error(`Failed to ${currentStatus === 'ACTIVE' ? 'pause' : 'resume'} workflow:`, error);
      toast.error(`Failed to ${currentStatus === 'ACTIVE' ? 'pause' : 'resume'} workflow`);
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'executing':
        return 'info';
      case 'failed':
        return 'danger';
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Get action icon
  const getActionIcon = (action: string) => {
    const actionLower = action.toLowerCase();
    if (actionLower.includes('email')) return faEnvelope;
    if (actionLower.includes('call') || actionLower.includes('phone')) return faPhone;
    if (actionLower.includes('document') || actionLower.includes('file')) return faFileText;
    if (actionLower.includes('schedule') || actionLower.includes('calendar')) return faCalendarAlt;
    if (actionLower.includes('follow')) return faUser;
    return faTasks;
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = date.getTime() - now.getTime();
      const hours = Math.ceil(diff / (1000 * 60 * 60));
      
      if (hours < 0) {
        return `Overdue by ${Math.abs(hours)} hours`;
      } else if (hours < 24) {
        return `In ${hours} hours`;
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return 'Invalid date';
    }
  };

  // Render task preview modal
  const TaskPreviewModal = () => (
    <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={getActionIcon(selectedTask?.action || '')} className="me-2" />
          Task Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedTask && (
          <div>
            <Row>
              <Col md={6}>
                <h6>Task Information</h6>
                <p><strong>Action:</strong> {selectedTask.action}</p>
                <p><strong>Agent Type:</strong> {selectedTask.agent_type}</p>
                <p><strong>Priority:</strong> 
                  <Badge bg={getPriorityColor(selectedTask.priority)} className="ms-2">
                    {selectedTask.priority}
                  </Badge>
                </p>
                <p><strong>Status:</strong> 
                  <Badge bg={getStatusColor(selectedTask.status)} className="ms-2">
                    {selectedTask.status}
                  </Badge>
                </p>
              </Col>
              <Col md={6}>
                <h6>Timing</h6>
                <p><strong>Scheduled:</strong> {formatDate(selectedTask.scheduled_for)}</p>
                <p><strong>Created:</strong> {new Date(selectedTask.created_at).toLocaleString()}</p>
                {selectedTask.completed_at && (
                  <p><strong>Completed:</strong> {new Date(selectedTask.completed_at).toLocaleString()}</p>
                )}
              </Col>
            </Row>
            
            {selectedTask.task_data && (
              <div className="mt-3">
                <h6>Task Data</h6>
                <pre className="bg-light p-3 rounded small">
                  {JSON.stringify(selectedTask.task_data, null, 2)}
                </pre>
              </div>
            )}
            
            {selectedTask.ai_response && (
              <div className="mt-3">
                <h6>AI Response</h6>
                <div className="bg-light p-3 rounded">
                  {selectedTask.ai_response}
                </div>
              </div>
            )}
            
            {selectedTask.error_message && (
              <Alert variant="danger" className="mt-3">
                <strong>Error:</strong> {selectedTask.error_message}
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {selectedTask?.status === 'pending' && selectedTask.requires_approval && (
          <Button 
            variant="success" 
            onClick={() => {
              if (selectedTask) {
                approveTask(selectedTask.task_id);
                setShowTaskModal(false);
              }
            }}
            disabled={approvingTasks.has(selectedTask.task_id)}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Approve Task
          </Button>
        )}
        <Button variant="secondary" onClick={() => setShowTaskModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (loading) {
    return (
      <Card className={className}>
        <Card.Header className="bg-info text-white">
          <FontAwesomeIcon icon={faRobot} className="me-2" />
          Agent Kairos Workflow
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="info" />
          <p className="mt-2 mb-0">Loading Agent Kairos workflow data...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <Card.Header className="bg-info text-white">
          <FontAwesomeIcon icon={faRobot} className="me-2" />
          Agent Kairos Workflow
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!workflowData || !workflowData.workflow) {
    return (
      <Card className={className}>
        <Card.Header className="bg-info text-white">
          <FontAwesomeIcon icon={faRobot} className="me-2" />
          Agent Kairos Workflow
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <FontAwesomeIcon icon={faRobot} className="me-2" />
            No Agent Kairos workflow found for this lead. Workflows are created automatically based on lead activity and engagement.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const { workflow, tasks, metrics } = workflowData;
  const pendingApprovalTasks = tasks.filter(task => task.status === 'pending' && task.requires_approval);
  const upcomingTasks = tasks.filter(task => task.status === 'pending').slice(0, 3);

  return (
    <>
      <Card className={className}>
        <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
          <div>
            <FontAwesomeIcon icon={faRobot} className="me-2" />
            Agent Kairos Workflow
          </div>
          <div className="d-flex gap-2">
            <Button 
              size="sm" 
              variant="light"
              onClick={fetchWorkflowData}
              title="Refresh workflow data"
            >
              <FontAwesomeIcon icon={faSync} />
            </Button>
            <Button 
              size="sm" 
              variant={workflow.status === 'ACTIVE' ? 'warning' : 'success'}
              onClick={() => toggleWorkflowStatus(workflow.instance_id, workflow.status)}
              title={workflow.status === 'ACTIVE' ? 'Pause workflow' : 'Resume workflow'}
            >
              <FontAwesomeIcon icon={workflow.status === 'ACTIVE' ? faPause : faPlay} />
            </Button>
          </div>
        </Card.Header>

        <Card.Body>
          {/* Workflow Status */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Workflow Status</h6>
              <Badge bg={workflow.status === 'ACTIVE' ? 'success' : 'warning'}>
                {workflow.status}
              </Badge>
            </div>
            <div className="progress mb-2">
              <div 
                className="progress-bar" 
                role="progressbar" 
                style={{ width: `${metrics.progress_percentage}%` }}
                aria-valuenow={metrics.progress_percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {metrics.progress_percentage}%
              </div>
            </div>
            <small className="text-muted">
              {metrics.completed_tasks} of {metrics.total_tasks} tasks completed
              {metrics.failed_tasks > 0 && ` • ${metrics.failed_tasks} failed`}
            </small>
          </div>

          {/* Pending Approval Tasks */}
          {pendingApprovalTasks.length > 0 && (
            <div className="mb-4">
              <h6 className="text-warning mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                Tasks Requiring Approval ({pendingApprovalTasks.length})
              </h6>
              {pendingApprovalTasks.map(task => (
                <div key={task.task_id} className="border rounded p-3 mb-2 border-warning">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon 
                          icon={getActionIcon(task.action)} 
                          className="me-2 text-info" 
                        />
                        <strong>{task.action}</strong>
                        <Badge bg={getPriorityColor(task.priority)} className="ms-2">
                          {task.priority}
                        </Badge>
                      </div>
                      <div className="text-muted small">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        Scheduled: {formatDate(task.scheduled_for)}
                      </div>
                      <div className="text-muted small">
                        Agent: {task.agent_type}
                      </div>
                    </div>
                    <div className="ms-3 d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => {
                          setSelectedTask(task);
                          setShowTaskModal(true);
                        }}
                        title="View task details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => approveTask(task.task_id)}
                        disabled={approvingTasks.has(task.task_id)}
                        title="Approve this task for execution"
                      >
                        {approvingTasks.has(task.task_id) ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <FontAwesomeIcon icon={faCheckCircle} />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upcoming Tasks */}
          {upcomingTasks.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-3">
                <FontAwesomeIcon icon={faTasks} className="me-2 text-primary" />
                Upcoming Tasks ({upcomingTasks.length})
              </h6>
              {upcomingTasks.map(task => (
                <div key={task.task_id} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon 
                        icon={getActionIcon(task.action)} 
                        className="me-2 text-muted" 
                      />
                      <div>
                        <div className="fw-bold">{task.action}</div>
                        <small className="text-muted">{formatDate(task.scheduled_for)}</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg={getStatusColor(task.status)}>{task.status}</Badge>
                      {!task.requires_approval && task.status === 'pending' && (
                        <Badge bg="info">Auto-Execute</Badge>
                      )}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          setSelectedTask(task);
                          setShowTaskModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No pending tasks */}
          {tasks.length === 0 && (
            <Alert variant="info">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              No tasks found for this workflow. The system may still be initializing or all tasks have been completed.
            </Alert>
          )}
        </Card.Body>

        <Card.Footer className="text-muted text-center">
          <small>
            <FontAwesomeIcon icon={faRobot} className="me-1" />
            Agent Kairos AI Automation System
          </small>
        </Card.Footer>
      </Card>

      {/* Task Details Modal */}
      <TaskPreviewModal />
    </>
  );
};

export default AgentKairosWorkflowPanel;